/**
 * The decision page — route: /brief/:id
 *
 * One vertical page that is the lifecycle, top to bottom:
 *
 *   1 Set up      the question, who decides, the deadline, what matters, the options
 *   2 Proposals   who has answered, who has not, remind / copy link / enter it for them
 *   3 Compare     the scoring grid and the ranking
 *   4 Decide      accept, accept with conditions, defer, reject — and put it on the plan
 *   5 Share       the public record link, print, archive
 *
 * A sticky bar at the top always says where the ball is and offers exactly
 * one action (lib/briefNextAction.ts). Replaces the three-tab BriefWorkspace
 * whose status pill hid the transitions in a dropdown and never said what to
 * do next.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import {
  loadBriefWorkspace,
  advanceBriefStatus,
  openBriefForProposals,
  sendBriefInvites,
  markOptionsInvited,
  upsertScore,
} from '../../services/brief/briefService';
import { useUserStore } from '../../stores/prioritization/userStore';
import type { Brief, BriefCriteria, BriefOptionWithProposal, BriefDecision } from '../../types/brief';
import { computeBriefNextAction, dueLabel, type BriefStage } from './lib/briefNextAction';
import { StageSection, type StageState } from './stages/StageSection';
import { SetupStage } from './stages/SetupStage';
import { ProposalsStage } from './stages/ProposalsStage';
import { CompareStage } from './stages/CompareStage';
import { ShareStage } from './stages/ShareStage';
import DecisionPanel from './DecisionPanel';
import { btnPrimary } from '@/components/mono/primitives';

const STAGE_ORDER: BriefStage[] = ['setup', 'proposals', 'compare', 'decide', 'share'];

function stageState(stage: BriefStage, live: BriefStage): StageState {
  const i = STAGE_ORDER.indexOf(stage);
  const l = STAGE_ORDER.indexOf(live);
  return i < l ? 'done' : i === l ? 'live' : 'upcoming';
}

export default function DecisionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUserId = useUserStore((s) => s.userId);

  const [brief, setBrief] = useState<Brief | null>(null);
  const [criteria, setCriteria] = useState<BriefCriteria[]>([]);
  const [options, setOptions] = useState<BriefOptionWithProposal[]>([]);
  const [decision, setDecision] = useState<BriefDecision | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [expanded, setExpanded] = useState<BriefStage | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await loadBriefWorkspace(id);
      setBrief(data.brief);
      setCriteria(data.criteria);
      setOptions(data.options);
      setDecision(data.decision);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Could not load this decision.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const next = useMemo(
    () =>
      brief
        ? computeBriefNextAction({
            brief,
            options,
            criteriaCount: criteria.filter((c) => c.name.trim()).length,
            decision,
            viewerId: currentUserId,
          })
        : null,
    [brief, options, criteria, decision, currentUserId],
  );

  const open = expanded ?? next?.liveStage ?? 'setup';
  const showError = (text: string) => setNotice({ tone: 'error', text });

  const openForProposals = async () => {
    if (!brief) return;
    setBusy(true);
    setNotice(null);
    try {
      const updated = await openBriefForProposals(brief.id);
      setBrief(updated);
      const withEmail = options.filter((o) => o.name.trim() && o.owner_email);
      const withoutEmail = options.filter((o) => o.name.trim() && !o.owner_email).length;
      let sentence = 'Open for proposals.';
      if (withEmail.length > 0) {
        const result = await sendBriefInvites(brief.id, withEmail.map((o) => o.id), 'invite');
        const sentIds = withEmail.map((o) => o.id).filter((oid) => !result.skipped.some((s) => s.option_id === oid));
        if (sentIds.length > 0) {
          await markOptionsInvited(sentIds, 'invite');
          const now = new Date().toISOString();
          setOptions((prev) => prev.map((o) => (sentIds.includes(o.id) ? { ...o, invited_at: now, last_reminded_at: now } : o)));
        }
        sentence += result.sent > 0 ? ` ${result.sent} owner${result.sent === 1 ? '' : 's'} got their link by email.` : '';
        if (result.skipped.length > 0) {
          sentence += ` ${result.skipped.length} could not be emailed (${result.skipped[0].reason}) — copy their links below.`;
        }
      }
      if (withoutEmail > 0) sentence += ` ${withoutEmail} option${withoutEmail === 1 ? ' has' : 's have'} no owner email — copy the link and send it yourself.`;
      setNotice({ tone: 'ok', text: sentence });
      setExpanded('proposals');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not open for proposals.');
    } finally {
      setBusy(false);
    }
  };

  const startComparing = async () => {
    if (!brief) return;
    setBusy(true);
    setNotice(null);
    try {
      setBrief(await advanceBriefStatus(brief.id, 'comparing'));
      setNotice({ tone: 'ok', text: 'Submissions are closed. Score each proposal against what matters; the ranking updates as you go.' });
      setExpanded('compare');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not start comparing.');
    } finally {
      setBusy(false);
    }
  };

  const runPrimary = () => {
    if (!next?.primary || !brief) return;
    const { label, stage } = next.primary;
    if (label === 'Open for proposals') return void openForProposals();
    if (label === 'Start comparing') return void startComparing();
    setExpanded(stage);
    document.getElementById(`stage-${STAGE_ORDER.indexOf(stage) + 1}-title`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleScoreChange = async (optionId: string, criteriaId: string, score: number) => {
    if (!currentUserId) return;
    const snapshot = options;
    setOptions((prev) =>
      prev.map((o) => {
        if (o.id !== optionId) return o;
        const existing = o.scores.find((s) => s.criteria_id === criteriaId);
        if (existing) return { ...o, scores: o.scores.map((s) => (s.criteria_id === criteriaId ? { ...s, score } : s)) };
        return {
          ...o,
          scores: [...o.scores, { id: crypto.randomUUID(), option_id: optionId, criteria_id: criteriaId, score, note: null, scored_by: currentUserId, scored_at: new Date().toISOString() }],
        };
      }),
    );
    try {
      await upsertScore(optionId, criteriaId, score, currentUserId);
    } catch (err) {
      setOptions(snapshot);
      showError(err instanceof Error && err.message ? `That score was not saved: ${err.message}` : 'That score was not saved. Check your connection and try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center" aria-busy="true">
        <div className="w-6 h-6 border-2 border-[var(--rule-bright)] border-t-[var(--text-sec)] rounded-full animate-spin" />
      </div>
    );
  }

  if (loadError || !brief || !next) {
    return (
      <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-base text-[var(--text-sec)] mb-4">{loadError ?? "We couldn't find that decision."}</p>
          <Link to="/brief" className="text-sm text-[var(--text-pri)] underline">Back to decisions</Link>
        </div>
      </div>
    );
  }

  const namedOptions = options.filter((o) => o.name.trim());
  const submitted = namedOptions.filter((o) => o.proposal).length;
  const due = dueLabel(brief.deadline);
  const isCreator = brief.created_by === currentUserId;
  const canOpen = brief.status === 'draft' && isCreator;
  const setupSummary = [
    brief.decision_authority ? `Decided by ${brief.decision_authority}` : null,
    due,
    `${criteria.length} thing${criteria.length === 1 ? '' : 's'} that matter`,
    `${namedOptions.length} option${namedOptions.length === 1 ? '' : 's'}`,
  ].filter(Boolean).join(' · ');

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      {/* Sticky: where the ball is, and the one thing to do */}
      <div className="sticky top-0 z-10 border-b border-[var(--rule)] bg-[var(--bg-raised)]">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-start gap-3">
            <Link to="/brief" aria-label="Back to decisions" className="flex-shrink-0 mt-1 text-[var(--text-ter)] hover:text-[var(--text-pri)] transition-colors">
              <ArrowLeftIcon className="w-5 h-5" />
            </Link>
            <div className="flex-1 min-w-0">
              <h1 className="font-heading font-semibold text-xl text-[var(--text-pri)] leading-snug" style={{ color: 'var(--text-pri)' }}>
                {brief.title}
              </h1>
              <p className="text-base text-[var(--text-sec)] mt-1">
                The ball is with <span className="text-[var(--text-pri)] font-medium">{next.ballWith}</span>
                <span className="text-[var(--text-dim)]"> · </span>
                {next.status}
              </p>
            </div>
            {next.primary && (
              <button
                type="button"
                onClick={runPrimary}
                disabled={busy}
                className="flex-shrink-0 inline-flex items-center h-11 px-5 bg-[var(--slate)] text-white font-mono text-xs uppercase tracking-[0.08em] hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                {busy ? 'Working…' : `${next.primary.label} →`}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-4">
        {notice && (
          <div
            role={notice.tone === 'error' ? 'alert' : 'status'}
            className={`px-4 py-3 text-sm flex items-start justify-between gap-4 border ${
              notice.tone === 'error' ? 'border-[var(--coral)]/30 bg-[var(--coral)]/10 text-[var(--coral)]' : 'border-[var(--green)]/30 bg-[var(--green)]/10 text-[var(--text-pri)]'
            }`}
          >
            <span>{notice.text}</span>
            <button type="button" onClick={() => setNotice(null)} className="text-xs underline flex-shrink-0">Dismiss</button>
          </div>
        )}

        <StageSection number={1} title="Set up" summary={setupSummary} state={stageState('setup', next.liveStage)} expanded={open === 'setup'} onToggle={() => setExpanded(open === 'setup' ? 'proposals' : 'setup')}>
          <SetupStage
            brief={brief}
            criteria={criteria}
            options={options}
            onBriefChange={setBrief}
            onCriteriaChange={setCriteria}
            onOptionsChange={(next) => setOptions(next.map((o) => ({ ...o, proposal: options.find((x) => x.id === o.id)?.proposal ?? null, scores: options.find((x) => x.id === o.id)?.scores ?? [] })))}
            onError={showError}
            footer={
              canOpen ? (
                <div className="pt-6 border-t border-[var(--rule)] flex flex-col sm:flex-row sm:items-center gap-3">
                  <p className="flex-1 text-sm text-[var(--text-sec)]">
                    Opening asks each owner for their proposal and fixes the options and what matters. You can still edit the question, deadline and background afterwards.
                  </p>
                  <button
                    type="button"
                    onClick={openForProposals}
                    disabled={busy}
                    {...btnPrimary()}
                  >
                    {busy ? 'Opening…' : 'Open for proposals →'}
                  </button>
                </div>
              ) : null
            }
          />
        </StageSection>

        <StageSection number={2} title="Proposals" summary={brief.status === 'draft' ? 'Owners are asked when you open this decision' : `${submitted} of ${namedOptions.length} in`} state={stageState('proposals', next.liveStage)} expanded={open === 'proposals'} onToggle={() => setExpanded(open === 'proposals' ? 'compare' : 'proposals')}>
          <ProposalsStage brief={brief} options={options} onOptionsChange={setOptions} onError={showError} />
          {brief.status === 'collecting' && isCreator && (
            <div className="pt-5 mt-5 border-t border-[var(--rule)] flex flex-col sm:flex-row sm:items-center gap-3">
              <p className="flex-1 text-sm text-[var(--text-sec)]">
                {submitted === namedOptions.length ? 'Everything is in.' : 'You can start comparing before every proposal is in; submissions close when you do.'}
              </p>
              <button type="button" onClick={startComparing} disabled={busy || submitted === 0} {...btnPrimary()}>
                Start comparing →
              </button>
            </div>
          )}
        </StageSection>

        <StageSection number={3} title="Compare" summary={brief.status === 'draft' || brief.status === 'collecting' ? 'Opens once you start comparing' : `${submitted} proposal${submitted === 1 ? '' : 's'} scored against ${criteria.length} thing${criteria.length === 1 ? '' : 's'}`} state={stageState('compare', next.liveStage)} expanded={open === 'compare'} onToggle={() => setExpanded(open === 'compare' ? 'decide' : 'compare')}>
          {submitted === 0 ? (
            <p className="text-sm text-[var(--text-dim)]">Nothing to compare until at least one proposal is in.</p>
          ) : (
            <CompareStage brief={brief} criteria={criteria} options={options} onScoreChange={handleScoreChange} />
          )}
        </StageSection>

        <StageSection number={4} title="Decide" summary={brief.status === 'decided' || brief.status === 'archived' ? 'On the record' : brief.status === 'deferred' ? 'Deferred' : 'Accept, accept with conditions, defer, or reject'} state={stageState('decide', next.liveStage)} expanded={open === 'decide'} onToggle={() => setExpanded(open === 'decide' ? 'share' : 'decide')}>
          {brief.status === 'draft' || brief.status === 'collecting' ? (
            <p className="text-sm text-[var(--text-dim)]">Available once you start comparing.</p>
          ) : (
            <DecisionPanel
              brief={brief}
              options={options}
              decision={decision}
              isCreator={isCreator}
              onDecisionSaved={(d) => {
                setDecision(d);
                const deferred = d.resolution_type === 'defer';
                setBrief({ ...brief, status: deferred ? 'deferred' : 'decided' });
                setNotice({
                  tone: 'ok',
                  text: deferred
                    ? 'Deferred. The record says when to look again; reopen it when that time comes.'
                    : 'Decided. The record is on this page and at the public link; you can also put it on the plan below.',
                });
              }}
              onReopened={(b) => {
                setDecision(null);
                setBrief({ ...brief, ...b });
                setNotice({ tone: 'ok', text: 'Reopened. The earlier decision and your reason are kept in the history.' });
              }}
              onNavigateToView={() => navigate(`/brief/view/${brief.view_token}`)}
            />
          )}
        </StageSection>

        <StageSection number={5} title="Share" summary={brief.status === 'draft' ? 'A public record link, once open' : 'Public record link · print · archive'} state={stageState('share', next.liveStage)} expanded={open === 'share'} onToggle={() => setExpanded(open === 'share' ? 'setup' : 'share')}>
          <ShareStage brief={brief} onBriefChange={setBrief} onError={showError} />
        </StageSection>
      </div>
    </div>
  );
}
